import { cloneDeep, isEmpty, noop } from 'lodash'
import { PropsWithChildren, Reducer, useContext } from 'react'
import React, { useCallback, useEffect, useMemo, useReducer } from 'react'
import {
  mediaCollectionsPerPage,
  decodeStringForUrl,
  filesPerPage,
  foldersPerPage,
  secondaryFoldersPerPage,
  fileTypeMapping,
} from '../../utils/mediaLibrary'
import {
  FileType,
  MLSortDirection,
  MLSortParams,
  MLViewType,
  ThreadFilter,
  ThreadSort,
} from '../constants'
import {
  useSetFolderPermission,
  useSetViewableParent,
} from '../hooks/useAccessControl'
import useMediaLibrarySearch from '../hooks/useMediaLibrarySearch'
import type {
  MLSort,
  MLSortDescriptors,
  MediaLibrarySearchCollectionResponse,
  MediaLibrarySearchFileResponse,
  MediaLibrarySearchFolderResponse,
  SearchQueryJson,
  ViewableParentSuccessResponse,
  Threads,
  FileDetailsResponse,
  StatusResponse,
  BulkJobItem,
  SearchFilterJson,
} from '../interfaces'

import { OperationStatus } from '../interfaces'
import { PublicLinksInfoContext } from './PublicLinksInfoProvider'
import { useGetBulkJobStatus } from '../hooks/useGetBulkJobStatus'
import { useSetHistory } from '../hooks/useSetHistory'
import { parseAndConvertSearchQuery } from '../../utils/search'
import { CustomMetadataContext } from './CustomMetadataProvider'
import { CheckValidObjectId } from '../components/MediaLibrary/utils'

// eslint-disable-next-line import/exports-last
export interface MediaLibraryQueryParams {
  sort: string | null
  view: MLViewType | null
  searchQuery: string | null
  q: string | null
  searchMode: string | null
  searchInputMode: string | null
  searchByTags: string | null
  isVersionIncludedInSearch: string | null
  fileType: FileType | "" | null
  minScore?: string | null
}

export interface MediaCollectionsQueryParams {
  sort: string | null
  view: MLViewType | null
  searchQuery: string
  q: string
}

interface ProviderProps {
  folderPath: string
  mediaCollectionId?: string
  queryParams?: MediaLibraryQueryParams & MediaCollectionsQueryParams
  setQueryParams?(
    s: React.SetStateAction<Partial<MediaLibraryQueryParams & MediaCollectionsQueryParams>>,
  ): void
  route: 'media-library' | 'search' | 'media-collections'
  directDetailRoute?: boolean
  suppressSearchRequests?: boolean
}

interface ContextMenuVisibleReducerProps {
  visible: true
  position: {
    x: number
    y: number
  }
}

interface ContextMenuHiddenReducerProps {
  visible: false
}

type ContextMenuReducerProps =
  | ContextMenuVisibleReducerProps
  | ContextMenuHiddenReducerProps

interface ReducerProps {
  secondarySort: MLSort
  secondarySortDescriptors: MLSortDescriptors
  folderPage: number
  secondaryFolderPage: number
  filePage: number
  mediaCollectionPage: number
  selectedFiles: MediaLibrarySearchFileResponse[]
  selectedFolders: MediaLibrarySearchFolderResponse[]
  selectedMediaCollections: MediaLibrarySearchCollectionResponse[]
  contextMenu: ContextMenuReducerProps
  drawerFolderPath: string
  copyItems: BulkJobItem[]
  moveItems: BulkJobItem[]
  renameFolderItem: BulkJobItem[]
  drawerSearchQuery: string
  drawerSearchFilter: string
  fileList: MediaLibrarySearchFileResponse[]
  recentFileFolderList: (MediaLibrarySearchFileResponse | MediaLibrarySearchFolderResponse)[]
  recentMediaCollectionList: MediaLibrarySearchCollectionResponse[]
  fileListError: string
  folderList: MediaLibrarySearchFolderResponse[]
  folderListError: string
  drawerFolderList: MediaLibrarySearchFolderResponse[]
  drawerFolderListError: string
  mediaCollectionList: MediaLibrarySearchCollectionResponse[]
  mediaCollectionListError: string
  loadingMediaCollections: boolean
  loadingFiles: boolean
  loadingFolders: boolean
  loadingDrawerFolders: boolean
  operations: Operations[]
  threads: Threads[]
  detailPageVersions: FileDetailsResponse[]
  selectedThread: Threads | null
  threadFilter: ThreadFilter
  threadSort: ThreadSort
  showNewThreadPanel: boolean
  videoTime: number
  folderPermission: string | undefined
  loadingFolderPermission: boolean
  viewableParent: ViewableParentSuccessResponse | string
  loadingViewableParent: boolean
  drawerViewableParent: ViewableParentSuccessResponse | string
  loadingDrawerViewableParent: boolean
  drawerFolderPermission: string
  loadingDrawerFolderPermission: boolean
  selectedBreadcrumbFolder: MediaLibrarySearchFolderResponse | undefined
  selectedBreadcrumbMediaCollection:
    | MediaLibrarySearchCollectionResponse
    | undefined
  urlState: UrlState
  queryParams?: MediaLibraryQueryParams & MediaCollectionsQueryParams
  openAdvancedSearch: boolean
  threadListUiLoaded: boolean
  openSemanticImageSearch: boolean
  unseenNotificationsCount: number
  generateImageDimension: {
    label: string
    value: string
  }
  generateImageNumber: number
  isAllSelected: boolean
  disableDetailNavigation: boolean
}

interface SetSearchQueryAction {
  type: MediaLibraryActions.SET_SEARCH_QUERY
  payload: string
}

interface SetQAction {
  type: MediaLibraryActions.SET_Q
  payload: string
}

interface SetSearchModeAction {
  type: MediaLibraryActions.SET_SEARCH_MODE
  payload: string | null
}

interface SetSearchInputModeAction {
  type: MediaLibraryActions.SET_SEARCH_INPUT_MODE
  payload: string
}

interface SetSearchByTagsAction {
  type: MediaLibraryActions.SET_SEARCH_BY_TAGS
  payload: boolean
}

interface SetisVersionIncludedInSearchAction {
  type: MediaLibraryActions.SET_IS_VERSION_INCLUDED_IN_SEARCH
  payload: boolean
}

interface ResetSearchAction {
  type: MediaLibraryActions.RESET_SEARCH
}

interface ResetMediaCollectionSearchAction {
  type: MediaLibraryActions.RESET_MEDIA_COLLECTION_SEARCH
}

interface ResetDrawerSearchAction {
  type: MediaLibraryActions.RESET_DRAWER_SEARCH
}

interface ResetMinScoreAction {
  type: MediaLibraryActions.RESET_MIN_SCORE
}

interface SetMinScoreAction {
  type: MediaLibraryActions.SET_MIN_SCORE
  payload: number
}

interface SetSearchFileTypeAction {
  type: MediaLibraryActions.SET_SEARCH_FILE_TYPE
  payload: FileType | ""
}

interface SetSearchQueryJsonAction {
  type: MediaLibraryActions.SET_SEARCH_QUERY_JSON
  payload: SearchQueryJson
}

interface SetMediaCollectionSearchQueryJsonAction {
  type: MediaLibraryActions.SET_MEDIA_COLLECTION_SEARCH_QUERY_JSON
  payload: SearchQueryJson
}

interface SetDrawerSearchQueryAction {
  type: MediaLibraryActions.SET_DRAWER_SEARCH_QUERY
  payload: string
}

interface SetDrawerSearchFilterAction {
  type: MediaLibraryActions.SET_DRAWER_SEARCH_FILTER
  payload: string
}
interface ViewAction {
  type: MediaLibraryActions.SET_VIEW
  payload: MLViewType
}

interface SortDirectionAction {
  type: MediaLibraryActions.SET_SORT_DIRECTION
  payload: MLSortDirection
}

interface SecondarySortDirectionAction {
  type: MediaLibraryActions.SET_SECONDARY_SORT_DIRECTION
  payload: MLSortDirection
}

interface SortParamsAction {
  type: MediaLibraryActions.SET_SORT_PARAM
  payload: MLSortParams
}

interface SetSortAction {
  type: MediaLibraryActions.SET_SORT
  payload: MLSort
}

interface SecondarySortParamsAction {
  type: MediaLibraryActions.SET_SECONDARY_SORT_PARAM
  payload: MLSortParams
}

interface LoadMoreAction {
  type:
    | MediaLibraryActions.LOAD_MORE_FILES
    | MediaLibraryActions.LOAD_MORE_FOLDERS
    | MediaLibraryActions.LOAD_MORE_SECONDARY_FOLDERS
    | MediaLibraryActions.LOAD_MORE_MEDIA_COLLECTIONS
}

interface ResetPageAction {
  type:
    | MediaLibraryActions.RESET_FILE_PAGE
    | MediaLibraryActions.RESET_FOLDER_PAGE
    | MediaLibraryActions.RESET_SECONDARY_FOLDER_PAGE
    | MediaLibraryActions.RESET_MEDIA_COLLECTIONS_PAGE
}

interface SelectFileAction {
  type: MediaLibraryActions.SELECT_FILE
  payload: {
    file: MediaLibrarySearchFileResponse
    event: Pick<
      React.MouseEvent<HTMLDivElement, MouseEvent>,
      'ctrlKey' | 'shiftKey' | 'metaKey'
    >
  }
}

interface SelectFolderAction {
  type: MediaLibraryActions.SELECT_FOLDER
  payload: {
    folder: MediaLibrarySearchFolderResponse
    event: Pick<
      React.MouseEvent<HTMLDivElement, MouseEvent>,
      'ctrlKey' | 'shiftKey' | 'metaKey'
    >
    isBreadcrumb?: boolean
  }
}

interface setSelectedBreadcrumbFolder {
  type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_FOLDER
  payload: {
    folder: MediaLibrarySearchFolderResponse | undefined
  }
}

interface setSelectedBreadcrumbMediaCollection {
  type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_MEDIA_COLLECTION
  payload: {
    mediaCollection: MediaLibrarySearchCollectionResponse | undefined
  }
}

interface SelectMediaCollectionAction {
  type: MediaLibraryActions.SELECT_MEDIA_COLLECTION
  payload: {
    mediaCollection: MediaLibrarySearchCollectionResponse
    event: Pick<
      React.MouseEvent<HTMLDivElement, MouseEvent>,
      'ctrlKey' | 'shiftKey' | 'metaKey'
    >
  }
}

interface SetSelectedFileAction {
  type: MediaLibraryActions.SET_SELECTED_FILES
  payload: MediaLibrarySearchFileResponse[]
}

interface SetSelectedFolderAction {
  type: MediaLibraryActions.SET_SELECTED_FOLDERS
  payload: MediaLibrarySearchFolderResponse[]
}

interface SetSelectedMediaCollectionAction {
  type: MediaLibraryActions.SET_SELECTED_MEDIA_COLLECTIONS
  payload: MediaLibrarySearchCollectionResponse[]
}

interface DeselectFileOrFolderAction {
  type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS
}

interface DeselectMediaCollectionAction {
  type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS
}

interface OpenContextMenuAction {
  type: MediaLibraryActions.OPEN_CONTEXT_MENU
  payload: {
    x: number
    y: number
  }
}

interface CloseContextMenuAction {
  type: MediaLibraryActions.CLOSE_CONTEXT_MENU
}

interface SetDrawerFolderAction {
  type: MediaLibraryActions.SET_DRAWER_FOLDER
  payload: string
}

interface SetCopyItems {
  type: MediaLibraryActions.SET_COPY_ITEMS
  payload: BulkJobItem[]
}

interface SetRenameItem {
  type: MediaLibraryActions.SET_RENAME_ITEM
  payload: BulkJobItem[]
}
interface SetMoveItems {
  type: MediaLibraryActions.SET_MOVE_ITEMS
  payload: BulkJobItem[]
}

interface SetFileList {
  type: MediaLibraryActions.SET_FILE_LIST
  payload: MediaLibrarySearchFileResponse[]
}

interface AddToRecentFileFolderList {
  type: MediaLibraryActions.ADD_TO_RECENT_FILE_FOLDER_LIST
  payload: (MediaLibrarySearchFileResponse | MediaLibrarySearchFolderResponse)[]
}

interface AddToRecentMediaCollectionList {
  type: MediaLibraryActions.ADD_TO_RECENT_MEDIA_COLLECTION_LIST
  payload: MediaLibrarySearchCollectionResponse[]
}

interface UpdateFile {
  type: MediaLibraryActions.UPDATE_FILES
  payload: (MediaLibrarySearchFileResponse | FileDetailsResponse)[]
}

interface UpdateFileVersionByVersionId {
  type: MediaLibraryActions.UPDATE_FILE_VERSIONS
  payload: ((MediaLibrarySearchFileResponse | FileDetailsResponse) & { _oldVersionId?: string })[]
}

interface UpdateFolder {
  type: MediaLibraryActions.UPDATE_FOLDERS
  payload: MediaLibrarySearchFolderResponse[]
}

interface UpdateMediaCollections {
  type: MediaLibraryActions.UPDATE_MEDIA_COLLECTIONS
  payload: MediaLibrarySearchCollectionResponse[]
}

interface RemoveFile {
  type: MediaLibraryActions.REMOVE_FILES
  payload: string[]
}

interface RemoveFileVersion {
  type: MediaLibraryActions.REMOVE_FILE_VERSIONS
  payload: string[]
}

interface RemoveFolder {
  type: MediaLibraryActions.REMOVE_FOLDERS
  payload: string[]
}

interface RemoveMediaCollection {
  type: MediaLibraryActions.REMOVE_MEDIA_COLLECTIONS
  payload: string[]
}

interface SetFileListError {
  type: MediaLibraryActions.SET_FILE_ERROR
  payload: string
}

interface SetFolderList {
  type: MediaLibraryActions.SET_FOLDER_LIST
  payload: MediaLibrarySearchFolderResponse[]
}

interface SetFolderListError {
  type: MediaLibraryActions.SET_FOLDER_ERROR
  payload: string
}

interface SetMediaCollectionList {
  type: MediaLibraryActions.SET_MEDIA_COLLECTION_LIST
  payload: MediaLibrarySearchCollectionResponse[]
}

interface SetMediaCollectionListError {
  type: MediaLibraryActions.SET_MEDIA_COLLECTION_ERROR
  payload: string
}

interface SetCurrentMediaCollection {
  type: MediaLibraryActions.SET_CURRENT_MEDIA_COLLECTION
  payload: string | undefined
}

interface SetDrawerFolderList {
  type: MediaLibraryActions.SET_DRAWER_FOLDER_LIST
  payload: MediaLibrarySearchFolderResponse[]
}

interface SetDrawerFolderListError {
  type: MediaLibraryActions.SET_DRAWER_FOLDER_ERROR
  payload: string
}

interface SetLoading {
  type:
    | MediaLibraryActions.SET_FILE_LOADING
    | MediaLibraryActions.SET_FOLDER_LOADING
    | MediaLibraryActions.SET_DRAWER_FOLDER_LOADING
    | MediaLibraryActions.SET_MEDIA_COLLECTION_LOADING
    | MediaLibraryActions.SET_FOLDER_PERMISSION_LOADING
    | MediaLibraryActions.SET_VIEWABLE_PARENT_LOADING
    | MediaLibraryActions.SET_DRAWER_FOLDER_PERMISSION_LOADING
    | MediaLibraryActions.SET_DRAWER_VIEWABLE_PARENT_LOADING
  payload: boolean
}

interface SetFolderPath {
  type: MediaLibraryActions.SET_FOLDER_PATH
  payload: string
}

interface Operations {
  jobId: string
  status: 'Pending' | 'Failed' | 'Partially succeeded' | 'Succeeded'
  name: string
  storageURL?: string
}

interface AddOperation {
  type: MediaLibraryActions.ADD_OPERATION
  payload: Operations
}

interface UpdateOperation {
  type: MediaLibraryActions.UPDATE_OPERATION
  payload: Operations[]
}

interface SetFolderPermission {
  type: MediaLibraryActions.SET_FOLDER_PERMISSION
  payload: string
}

interface SetDrawerFolderPermission {
  type: MediaLibraryActions.SET_DRAWER_FOLDER_PERMISSION
  payload: string
}

interface SetViewableParent {
  type: MediaLibraryActions.SET_VIEWABLE_PARENT
  payload: ViewableParentSuccessResponse | string
}

interface SetDrawerViewableParent {
  type: MediaLibraryActions.SET_DRAWER_VIEWABLE_PARENT
  payload: ViewableParentSuccessResponse | string
}

interface SetMediaCollectionSearchQueryAction {
  type: MediaLibraryActions.SET_MEDIA_COLLECTION_SEARCH_QUERY
  payload: string
}
interface SetOpenAdvancedSearch {
  type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH
  payload: boolean
}

interface SetOpenSemanticImageSearch {
  type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH
  payload: boolean
}

interface SetUnseenNotificationsCount {
  type: MediaLibraryActions.SET_UNSEEN_NOTIFICATIONS_COUNT
  payload: number
}

interface SetThreads {
  type: MediaLibraryActions.SET_THREADS
  payload: Threads[]
}

interface SetDetailPageVersions {
  type: MediaLibraryActions.SET_DETAIL_PAGE_VERSIONS
  payload: FileDetailsResponse[]
}

interface SetSelectedThread {
  type: MediaLibraryActions.SET_SELECTED_THREAD
  payload: Threads | null
}

interface NavigateThread {
  type:
    | MediaLibraryActions.SELECT_NEXT_THREAD
    | MediaLibraryActions.SELECT_PREVIOUS_THREAD
  payload: Threads[]
}

interface SetThreadFilter {
  type: MediaLibraryActions.SET_THREAD_FILTER
  payload: ThreadFilter
}

interface SetThreadSort {
  type: MediaLibraryActions.SET_THREAD_SORT
  payload: ThreadSort
}

interface ShowNewThreadPanel {
  type: MediaLibraryActions.SHOW_NEW_THREAD_PANEL
  payload: boolean
}

interface SetVideoTime {
  type: MediaLibraryActions.SET_VIDEO_TIME
  payload: number
}

interface ThreadListUiLoaded {
  type: MediaLibraryActions.THREAD_LIST_UI_LOADED
}

interface SetGeneratedImagesDimension {
  type: MediaLibraryActions.SET_GENERATED_IMAGES_DIMENSION
  payload: {
    label: string
    value: string
  }
}

interface SetGeneratedImagesNumber {
  type: MediaLibraryActions.SET_GENERATED_IMAGES_NUMBER
  payload: number
}

interface SelectAll {
  type: MediaLibraryActions.SELECT_ALL
  payload: "/search" | "/media-library" | "/media-collections"
}

interface SetIsAllSelectedFalse {
  type: MediaLibraryActions.SET_IS_ALL_SELECTED_FALSE
}

interface SetDisableDetailNavigationAction {
  type: MediaLibraryActions.SET_DISABLE_DETAIL_NAVIGATION
  payload: boolean
}

export interface ContextProps extends ReducerProps {
  folderPath: string
  loading: boolean
  mediaCollectionRoute: boolean | undefined
  route?: 'media-library' | 'search' | 'media-collections'
  refetchFiles(): Promise<void>
  refetchFolders(): Promise<void>
  refetchMediaCollections(): Promise<void>
  refetchViewableParent(): Promise<void>
  refetchFolderPermission(): Promise<void>
  refetch(): Promise<void>
  refetchDrawerFolders(): Promise<void>
  abortFilesRequest(): void
  abortFoldersRequest(): void
  abortMediaCollectionsRequest(): void
  urlState: UrlState
  queryParams?: MediaLibraryQueryParams & MediaCollectionsQueryParams
}

export type MediaLibraryActionType =
  | ViewAction
  | SortDirectionAction
  | SecondarySortDirectionAction
  | SortParamsAction
  | SetSortAction
  | SecondarySortParamsAction
  | LoadMoreAction
  | ResetPageAction
  | SelectFileAction
  | SelectFolderAction
  | setSelectedBreadcrumbFolder
  | setSelectedBreadcrumbMediaCollection
  | SelectMediaCollectionAction
  | SetSelectedFileAction
  | SetSelectedFolderAction
  | DeselectFileOrFolderAction
  | DeselectMediaCollectionAction
  | SetSearchQueryAction
  | SetQAction
  | SetSearchModeAction
  | SetSearchFileTypeAction
  | OpenContextMenuAction
  | CloseContextMenuAction
  | SetDrawerFolderAction
  | SetCopyItems
  | SetMoveItems
  | SetRenameItem
  | SetSearchQueryJsonAction
  | SetMediaCollectionSearchQueryJsonAction
  | SetDrawerSearchQueryAction
  | SetDrawerSearchFilterAction
  | ResetSearchAction
  | ResetMediaCollectionSearchAction
  | ResetDrawerSearchAction
  | ResetMinScoreAction
  | SetMinScoreAction
  | SetFileList
  | SetFolderList
  | SetMediaCollectionList
  | SetDrawerFolderList
  | SetLoading
  | SetFolderPath
  | AddOperation
  | UpdateOperation
  | SetFolderPermission
  | SetDrawerFolderPermission
  | SetViewableParent
  | SetDrawerViewableParent
  | SetMediaCollectionSearchQueryAction
  | SetSelectedMediaCollectionAction  
  | SetOpenAdvancedSearch
  | SetOpenSemanticImageSearch
  | SetThreads
  | SetSelectedThread
  | NavigateThread
  | SetThreadFilter
  | SetThreadSort
  | ShowNewThreadPanel
  | SetVideoTime
  | SetDetailPageVersions
  | ThreadListUiLoaded
  | SetFileListError
  | SetFolderListError
  | SetMediaCollectionListError
  | SetDrawerFolderListError
  | SetUnseenNotificationsCount
  | SetGeneratedImagesDimension
  | SetGeneratedImagesNumber
  | SetSearchInputModeAction
  | SetSearchByTagsAction
  | SetisVersionIncludedInSearchAction
  | AddToRecentFileFolderList
  | AddToRecentMediaCollectionList
  | UpdateFile
  | UpdateFileVersionByVersionId
  | UpdateFolder
  | UpdateMediaCollections
  | RemoveFile
  | RemoveFileVersion
  | RemoveFolder
  | RemoveMediaCollection
  | SelectAll
  | SetIsAllSelectedFalse
  | SetDisableDetailNavigationAction


export enum MediaLibraryActions {
  SET_VIEW = 'SET_VIEW',
  SET_SORT_DIRECTION = 'SET_SORT_DIRECTION',
  SET_SORT_PARAM = 'SET_SORT_PARAM',
  SET_SORT = 'SET_SORT',
  SET_SECONDARY_SORT_DIRECTION = 'SET_SECONDARY_SORT_DIRECTION',
  SET_SECONDARY_SORT_PARAM = 'SET_SECONDARY_SORT_PARAM',
  LOAD_MORE_FOLDERS = 'LOAD_MORE_FOLDERS',
  LOAD_MORE_SECONDARY_FOLDERS = 'LOAD_MORE_SECONDARY_FOLDERS',
  LOAD_MORE_FILES = 'LOAD_MORE_FILES',
  LOAD_MORE_MEDIA_COLLECTIONS = 'LOAD_MORE_MEDIA_COLLECTIONS',
  SELECT_FILE = 'SELECT_FILE',
  SET_SELECTED_FILES = 'SET_SELECTED_FILES',
  SELECT_FOLDER = 'SELECT_FOLDER',
  SET_SELECTED_BREADCRUMB_FOLDER = 'SET_SELECTED_BREADCRUMB_FOLDER',
  SET_SELECTED_BREADCRUMB_MEDIA_COLLECTION = 'SET_SELECTED_BREADCRUMB_MEDIA_COLLECTION',
  SELECT_MEDIA_COLLECTION = 'SELECT_MEDIA_COLLECTION',
  SET_SELECTED_FOLDERS = 'SET_SELECTED_FOLDERS',
  DESELECT_ALL_FILES_AND_FOLDERS = 'DESELECT_ALL_FILES_AND_FOLDERS',
  DESELECT_ALL_MEDIA_COLLECTIONS = 'DESELECT_ALL_MEDIA_COLLECTIONS',
  SET_SEARCH_QUERY = 'SET_SEARCH_QUERY',
  SET_Q = 'SET_Q',
  SET_SEARCH_MODE = 'SET_SEARCH_MODE',
  SET_SEARCH_INPUT_MODE = 'SET_SEARCH_INPUT_MODE',
  SET_SEARCH_BY_TAGS = 'SET_SEARCH_BY_TAGS',
  SET_IS_VERSION_INCLUDED_IN_SEARCH = 'SET_IS_VERSION_INCLUDED_IN_SEARCH',
  OPEN_CONTEXT_MENU = 'OPEN_CONTEXT_MENU',
  CLOSE_CONTEXT_MENU = 'CLOSE_CONTEXT_MENU',
  RESET_FOLDER_PAGE = 'RESET_FOLDER_PAGE',
  RESET_MEDIA_COLLECTIONS_PAGE = 'RESET_MEDIA_COLLECTIONS_PAGE',
  RESET_SECONDARY_FOLDER_PAGE = 'RESET_SECONDARY_FOLDER_PAGE',
  RESET_FILE_PAGE = 'RESET_FILE_PAGE',
  SET_DRAWER_FOLDER = 'SET_DRAWER_FOLDER',
  SET_COPY_ITEMS = 'SET_COPY_ITEMS',
  SET_MOVE_ITEMS = 'SET_MOVE_ITEMS',
  SET_SEARCH_QUERY_JSON = 'SET_SEARCH_QUERY_JSON',
  SET_MEDIA_COLLECTION_SEARCH_QUERY_JSON = 'SET_MEDIA_COLLECTION_SEARCH_QUERY_JSON',
  SET_DRAWER_SEARCH_QUERY = 'SET_DRAWER_SEARCH_QUERY',
  SET_DRAWER_SEARCH_FILTER = 'SET_DRAWER_SEARCH_FILTER',
  SET_SEARCH_FILE_TYPE = 'SET_SEARCH_FILE_TYPE',
  RESET_SEARCH = 'RESET_SEARCH',
  RESET_DRAWER_SEARCH = 'RESET_DRAWER_SEARCH',
  RESET_MIN_SCORE = 'RESET_MIN_SCORE',
  SET_MIN_SCORE = 'SET_MIN_SCORE',
  SET_FOLDER_LIST = 'SET_FOLDER_LIST',
  SET_FOLDER_ERROR = 'SET_FOLDER_ERROR',
  SET_FILE_LIST = 'SET_FILE_LIST',
  SET_FILE_ERROR = 'SET_FILE_ERROR',
  SET_MEDIA_COLLECTION_LIST = 'SET_MEDIA_COLLECTION_LIST',
  SET_MEDIA_COLLECTION_ERROR = 'SET_MEDIA_COLLECTION_ERROR',
  SET_FILE_TYPE_QUERY = 'SET_FILE_TYPE_QUERY',
  SET_DRAWER_FOLDER_LIST = 'SET_DRAWER_FOLDER_LIST',
  SET_DRAWER_FOLDER_ERROR = 'SET_DRAWER_FOLDER_ERROR',
  SET_FOLDER_LOADING = 'SET_FOLDER_LOADING',
  SET_FILE_LOADING = 'SET_FILE_LOADING',
  SET_MEDIA_COLLECTION_LOADING = 'SET_MEDIA_COLLECTION_LOADING',
  SET_DRAWER_FOLDER_LOADING = 'SET_DRAWER_FOLDER_LOADING',
  SET_FOLDER_PATH = 'SET_FOLDER_PATH',
  ADD_OPERATION = 'ADD_OPERATION',
  UPDATE_OPERATION = 'UPDATE_OPERATION',
  SET_CURRENT_MEDIA_COLLECTION = 'SET_CURRENT_MEDIA_COLLECTION',
  SET_FOLDER_PERMISSION = 'SET_FOLDER_PERMISSION',
  SET_FOLDER_PERMISSION_LOADING = 'SET_FOLDER_PERMISSION_LOADING',
  SET_DRAWER_FOLDER_PERMISSION = 'SET_DRAWER_FOLDER_PERMISSION',
  SET_DRAWER_FOLDER_PERMISSION_LOADING = 'SET_DRAWER_FOLDER_PERMISSION_LOADING',
  SET_VIEWABLE_PARENT = 'SET_VIEWABLE_PARENT',
  SET_VIEWABLE_PARENT_LOADING = 'SET_VIEWABLE_PARENT_LOADING',
  SET_DRAWER_VIEWABLE_PARENT = 'SET_DRAWER_VIEWABLE_PARENT',
  SET_DRAWER_VIEWABLE_PARENT_LOADING = 'SET_DRAWER_VIEWABLE_PARENT_LOADING',
  SET_MEDIA_COLLECTION_SEARCH_QUERY = 'SET_MEDIA_COLLECTION_SEARCH_QUERY',
  SET_SELECTED_MEDIA_COLLECTIONS = 'SET_SELECTED_MEDIA_COLLECTIONS',
  RESET_MEDIA_COLLECTION_SEARCH = 'RESET_MEDIA_COLLECTION_SEARCH',
  SET_OPEN_ADVANCED_SEARCH = 'SET_OPEN_ADVANCED_SEARCH',
  SET_OPEN_SEMANTIC_IMAGE_SEARCH = 'SET_OPEN_SEMANTIC_IMAGE_SEARCH',
  SET_UNSEEN_NOTIFICATIONS_COUNT = 'SET_UNSEEN_NOTIFICATIONS_COUNT',
  SET_THREADS = 'SET_THREADS',
  SET_SELECTED_THREAD = 'SET_SELECTED_THREAD',
  SELECT_NEXT_THREAD = 'SELECT_NEXT_THREAD',
  SELECT_PREVIOUS_THREAD = 'SELECT_PREVIOUS_THREAD',
  SET_THREAD_FILTER = 'SET_THREAD_FILTER',
  SET_THREAD_SORT = 'SET_THREAD_SORT',
  SHOW_NEW_THREAD_PANEL = 'SHOW_NEW_THREAD_PANEL',
  SET_VIDEO_TIME = 'SET_VIDEO_TIME',
  SET_DETAIL_PAGE_VERSIONS = 'SET_DETAIL_PAGE_VERSIONS',
  THREAD_LIST_UI_LOADED = 'THREAD_LIST_UI_LOADED',
  SET_GENERATED_IMAGES_DIMENSION = 'SET_GENERATED_IMAGES_DIMENSION',
  SET_GENERATED_IMAGES_NUMBER = 'SET_GENERATED_IMAGES_NUMBER',
  SET_RENAME_ITEM = 'SET_RENAME_ITEM',
  ADD_TO_RECENT_FILE_FOLDER_LIST = 'ADD_TO_RECENT_FILE_FOLDER_LIST',
  ADD_TO_RECENT_MEDIA_COLLECTION_LIST = 'ADD_TO_RECENT_MEDIA_COLLECTION_LIST',
  UPDATE_FILES = 'UPDATE_FILES',
  UPDATE_FILE_VERSIONS = 'UPDATE_FILE_VERSIONS',
  UPDATE_FOLDERS = 'UPDATE_FOLDERS',
  REMOVE_FILES = 'REMOVE_FILES',
  REMOVE_FILE_VERSIONS = 'REMOVE_FILE_VERSIONS',
  REMOVE_FOLDERS = 'REMOVE_FOLDERS',
  REMOVE_MEDIA_COLLECTIONS = 'REMOVE_MEDIA_COLLECTIONS',
  UPDATE_MEDIA_COLLECTIONS = 'UPDATE_MEDIA_COLLECTIONS',
  SELECT_ALL = 'SELECT_ALL',
  SET_IS_ALL_SELECTED_FALSE = 'SET_IS_ALL_SELECTED_FALSE',
  SET_DISABLE_DETAIL_NAVIGATION = 'SET_DISABLE_DETAIL_NAVIGATION',
}

export interface UrlState {
  view: MLViewType
  sort: MLSort
  sortDescriptors: MLSortDescriptors
  searchQueryJson: SearchQueryJson
  searchQuery: string
  q: string
  searchMode: string | null
  searchInputMode: string
  isVersionIncludedInSearch?: boolean
  searchByTags?: boolean
  fileType: FileType | ""
  currentMediaCollection?: string
  folderPath: string
  minScore?: number
}

export const defaultUrlState: UrlState = {
  sort: 'DESC_CREATED',
  view: MLViewType.GRID,
  sortDescriptors: {
    sortDirection: MLSortDirection.DESC,
    sortParam: MLSortParams.CREATED,
  },
  searchQuery: '',
  searchQueryJson: {
    boolean: 'and',
    filterGroups: [],
  },
  q: '',
  searchMode: 'keyword',
  searchInputMode: 'builder',
  searchByTags: true,
  isVersionIncludedInSearch: false,
  fileType: "",
  folderPath: '/',
}

export const defaultInitialState: ReducerProps = {
  secondarySort: 'DESC_CREATED',
  secondarySortDescriptors: {
    sortDirection: MLSortDirection.DESC,
    sortParam: MLSortParams.CREATED,
  },
  folderPage: 0,
  secondaryFolderPage: 0,
  filePage: 0,
  mediaCollectionPage: 0,
  selectedFiles: [],
  selectedFolders: [],
  selectedMediaCollections: [],
  contextMenu: {
    visible: false,
  },
  drawerFolderPath: '/',
  copyItems: [],
  moveItems: [],
  renameFolderItem:[],
  drawerSearchQuery: '',
  drawerSearchFilter: '',
  fileList: [],
  recentFileFolderList: [],
  recentMediaCollectionList: [],
  fileListError: '',
  folderList: [],
  folderListError: '',
  drawerFolderList: [],
  drawerFolderListError: '',
  mediaCollectionList: [],
  mediaCollectionListError: '',
  loadingFiles: false,
  loadingFolders: false,
  loadingDrawerFolders: false,
  loadingMediaCollections: false,
  operations: [],
  folderPermission: '',
  loadingFolderPermission: false,
  drawerFolderPermission: '',
  loadingDrawerFolderPermission: false,
  detailPageVersions: [],
  threads: [],
  selectedThread: null,
  threadFilter: ThreadFilter.UNRESOLVED,
  threadSort: ThreadSort.DESC_UPDATED,
  showNewThreadPanel: false,
  videoTime: -1,
  viewableParent: {
    asset: {
      assetId: '',
      name: '',
      assetPath: '/',
    },
  },
  loadingViewableParent: true,
  drawerViewableParent: {
    asset: {
      assetId: '',
      name: '',
      assetPath: '/',
    },
  },
  loadingDrawerViewableParent: false,
  selectedBreadcrumbFolder: undefined,
  selectedBreadcrumbMediaCollection: undefined,
  urlState: defaultUrlState,
  openAdvancedSearch: false,
  threadListUiLoaded: false,
  openSemanticImageSearch: false,
  unseenNotificationsCount: 0,
  generateImageDimension: { 
    label: '1024 x 1024', value: '1024 x 1024'
  },
  generateImageNumber: 4,
  isAllSelected: false,
  disableDetailNavigation: false,
}

export const MLContext = React.createContext<
  [
    ContextProps & UrlState,
    React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>,
  ]
>([
  {
    ...defaultInitialState,
    ...defaultUrlState,
    loading: false,
    mediaCollectionRoute: false,
    refetchFiles: () => Promise.resolve(),
    refetchFolders: () => Promise.resolve(),
    refetchMediaCollections: () => Promise.resolve(),
    refetchViewableParent: () => Promise.resolve(),
    refetchFolderPermission: () => Promise.resolve(),
    refetch: () => Promise.resolve(),
    refetchDrawerFolders: () => Promise.resolve(),
    abortFilesRequest: () => {},
    abortFoldersRequest: () => {},
    abortMediaCollectionsRequest: () => {},
  },
  () => noop,
])


export const MLProvider = ({
  folderPath,
  mediaCollectionId,
  children,
  queryParams,
  setQueryParams,
  route,
  directDetailRoute,
  suppressSearchRequests = false,
}: PropsWithChildren<ProviderProps>): React.ReactElement => {

  const { cmFields: customMetadataFields } = useContext(CustomMetadataContext)
  const { isPublicLink } = useContext(PublicLinksInfoContext)

  const setHistory = useSetHistory()

  // Derive mediaCollectionRoute for backward compatibility
  const mediaCollectionRoute = route === 'media-collections'

  const urlState = useMemo(() => {
    const urlStateCopy: UrlState = cloneDeep(defaultUrlState)

    if (mediaCollectionId) {
      urlStateCopy.currentMediaCollection = mediaCollectionId
    }

    if (folderPath) {
      urlStateCopy.folderPath = folderPath
    }

    const sortQueryValue = queryParams?.sort
    if (sortQueryValue) {
      const [sortDirection, sortParam] = sortQueryValue.split('_')

      if (
        sortDirection &&
        sortParam &&
        ['ASC', 'DESC'].includes(sortDirection) &&
        [
          'CREATED',
          'UPDATED',
          'NAME',
          'HEIGHT',
          'WIDTH',
          'SIZE',
          'RELEVANCE',
        ].includes(sortParam)
      ) {
        urlStateCopy.sortDescriptors.sortDirection =
          sortDirection as MLSortDirection
        urlStateCopy.sortDescriptors.sortParam = sortParam as MLSortParams
        urlStateCopy.sort = sortQueryValue as MLSort
      }
    }

    const viewQueryValue = queryParams?.view
    if (viewQueryValue && ['GRID', 'LIST'].includes(viewQueryValue)) {
      urlStateCopy.view = viewQueryValue
    }
    const searchInputModeValue = queryParams?.searchInputMode
    if (searchInputModeValue) {
      urlStateCopy.searchInputMode = searchInputModeValue
    }
    const q = queryParams?.q
    if (q) {
      urlStateCopy.q = q
    }
    const searchModeValue = queryParams?.searchMode
    if (searchModeValue) {
      urlStateCopy.searchMode = searchModeValue
    }
    const searchByTagsValue = queryParams?.searchByTags
    if (searchByTagsValue) {
      urlStateCopy.searchByTags = searchByTagsValue === "true"
    } else {
      urlStateCopy.searchByTags = true
    }
    const isVersionIncludedInSearchValue =
      queryParams?.isVersionIncludedInSearch
    if (isVersionIncludedInSearchValue) {
      urlStateCopy.isVersionIncludedInSearch =
        isVersionIncludedInSearchValue === "true"
    }
    const minScore = parseFloat(queryParams?.minScore ?? '')
    if (!isNaN(minScore)) {
      urlStateCopy.minScore = minScore
    }
    const fileTypeValue = queryParams?.fileType
    if (fileTypeValue) {
      urlStateCopy.fileType = fileTypeValue
    }
    const searchQueryValue = queryParams?.searchQuery
    if (searchQueryValue) {
      urlStateCopy.searchQuery = searchQueryValue
      try {
        // Use the new combined function that handles everything
        const searchQueryJson = parseAndConvertSearchQuery(
          searchQueryValue,
          customMetadataFields,
        ) as SearchQueryJson
        
        urlStateCopy.searchQueryJson = searchQueryJson
      } catch (err) {
        // Any error (complex query, parsing failure, etc.) → query mode
        urlStateCopy.searchInputMode = "query"
        urlStateCopy.q = ''
        urlStateCopy.searchByTags = false
        urlStateCopy.searchMode = null
        urlStateCopy.minScore = undefined
        urlStateCopy.fileType = ""
      }
    }


    return urlStateCopy
  }, [queryParams, mediaCollectionId, folderPath, customMetadataFields])

  const initialState: ReducerProps = useMemo(() => {
    return cloneDeep(defaultInitialState)
  }, [defaultInitialState])

  function updateByKeyOnly<T extends Record<string, any>>(
    base: T[],
    incoming: T[],
    key: keyof T
  ): T[] {
    if (!base?.length || !incoming?.length) return base
    const patch = new Map<string, T>(incoming.map(x => [String(x[key]), x]))
    return base.map(item => {
      const p = patch.get(String(item[key]))
      return p ? { ...item, ...p } : item
    })
  }
  
  function updateRecentOnly(
    recent: (MediaLibrarySearchFileResponse | MediaLibrarySearchFolderResponse)[],
    incoming: (MediaLibrarySearchFileResponse | MediaLibrarySearchFolderResponse)[]
  ) {
    if (!recent?.length || !incoming?.length) return recent
    const keyOf = (x: any) => ('fileId' in x ? `f:${x.fileId}` : `d:${x.folderId}`)
    const patch = new Map(incoming.map(x => [keyOf(x), x]))
    return recent.map(item => {
      const k = keyOf(item)
      const p = patch.get(k)
      return p ? { ...item, ...p } : item
    })
  }

  const [state, stateDispatch] = useReducer<
    Reducer<ReducerProps, MediaLibraryActionType | MediaLibraryActionType[]>,
    ReducerProps
  >(
    (prevState, inputAction) => {
      const actions: MediaLibraryActionType[] = Array.isArray(inputAction)
        ? inputAction
        : [inputAction]

      const stateUpdate = actions.reduce<Partial<ReducerProps>>(
        (acc, action) => {
          switch (action.type) {
            case MediaLibraryActions.SET_THREADS:
              return {
                ...acc,
                threads: action.payload,
              }

            case MediaLibraryActions.SET_SELECTED_THREAD:
              if (action.payload?.threadId) {
                setHistory(action.payload.threadId)
              } else {
                setHistory(null)
              }
              return {
                ...acc,
                selectedThread: action.payload,
              }

            case MediaLibraryActions.SELECT_NEXT_THREAD: {
              const currentIndex = action.payload.findIndex(
                thread =>
                  thread.threadId === prevState.selectedThread?.threadId,
              )
              const nextIndex =
                currentIndex < action.payload.length - 1
                  ? currentIndex + 1
                  : currentIndex
              if (action.payload[nextIndex].threadId) {
                setHistory(action.payload[nextIndex].threadId)
              } else {
                setHistory(null)
              }
              return {
                ...acc,
                selectedThread: action.payload[nextIndex] || null,
              }
            }

            case MediaLibraryActions.SET_THREAD_FILTER:
              return {
                ...acc,
                threadFilter: action.payload,
              }

            case MediaLibraryActions.SET_THREAD_SORT:
              return {
                ...acc,
                threadSort: action.payload,
              }

            case MediaLibraryActions.SHOW_NEW_THREAD_PANEL:
              return {
                ...acc,
                showNewThreadPanel: action.payload,
              }

            case MediaLibraryActions.SET_VIDEO_TIME:
              return {
                ...acc,
                videoTime: action.payload,
              }

            case MediaLibraryActions.SELECT_PREVIOUS_THREAD: {
              const currentIndex = action.payload.findIndex(
                thread =>
                  thread.threadId === prevState.selectedThread?.threadId,
              )
              const prevIndex =
                currentIndex > 0 ? currentIndex - 1 : currentIndex
              if (action.payload[prevIndex].threadId) {
                setHistory(action.payload[prevIndex].threadId)
              } else {
                setHistory(null)
              }
              return {
                ...acc,
                selectedThread: action.payload[prevIndex] || null,
              }
            }

            case MediaLibraryActions.SET_FOLDER_PERMISSION:
              return { ...acc, folderPermission: action.payload }

            case MediaLibraryActions.SET_VIEWABLE_PARENT:
              return { ...acc, viewableParent: action.payload }

            case MediaLibraryActions.SET_VIEWABLE_PARENT_LOADING:
              return { ...acc, loadingViewableParent: action.payload }

            case MediaLibraryActions.SET_FILE_LIST:
              return {
                ...acc,
                fileList: action.payload,
                selectedFiles: prevState.isAllSelected
                  ? action.payload
                  : prevState.selectedFiles,
              }

            case MediaLibraryActions.ADD_TO_RECENT_FILE_FOLDER_LIST:
              return {
                ...acc,
                recentFileFolderList: [
                  ...action.payload,
                  ...(prevState.recentFileFolderList || []),
                ],
              }

            case MediaLibraryActions.ADD_TO_RECENT_MEDIA_COLLECTION_LIST:
              return {
                ...acc,
                recentMediaCollectionList: [
                  ...action.payload,
                  ...(prevState.recentMediaCollectionList || []),
                ],
              }

            case MediaLibraryActions.UPDATE_FILES: {
              return {
                ...acc,
                fileList: updateByKeyOnly(
                  prevState.fileList,
                  action.payload,
                  'fileId',
                ),
                selectedFiles: updateByKeyOnly(
                  prevState.selectedFiles,
                  action.payload,
                  'fileId',
                ),
                recentFileFolderList: updateRecentOnly(
                  prevState.recentFileFolderList,
                  action.payload as MediaLibrarySearchFileResponse[],
                ),
              }
            }

            case MediaLibraryActions.UPDATE_FILE_VERSIONS: {
              // Update file-versions by matching old versionInfo.id and replacing with new object
              if (!action.payload?.length) {
                return acc
              }
              // Create map using _oldVersionId if present, otherwise use current versionInfo.id
              const versionPatch = new Map(
                action.payload.map(x => {
                  const { _oldVersionId, ...cleanPayload } = x as any
                  return [_oldVersionId || x.versionInfo?.id, cleanPayload]
                }),
              )
              return {
                ...acc,
                fileList: prevState.fileList.map(item => {
                  const replacement = versionPatch.get(item.versionInfo?.id)
                  // If found, replace entire object (not merge)
                  return replacement ? replacement : item
                }),
              }
            }

            case MediaLibraryActions.UPDATE_FOLDERS: {
              return {
                ...acc,
                folderList: updateByKeyOnly(
                  prevState.folderList,
                  action.payload,
                  'folderId',
                ),
                selectedFolders: updateByKeyOnly(
                  prevState.selectedFolders,
                  action.payload,
                  'folderId',
                ),
                recentFileFolderList: updateRecentOnly(
                  prevState.recentFileFolderList,
                  action.payload,
                ),
              }
            }

            case MediaLibraryActions.UPDATE_MEDIA_COLLECTIONS: {
              const patch = new Map(
                action.payload.map(x => [x.mediaCollectionId, x]),
              )
              return {
                ...acc,
                mediaCollectionList: prevState.mediaCollectionList.map(mc => {
                  const p = patch.get(mc.mediaCollectionId)
                  return p ? { ...mc, ...p } : mc
                }),
                recentMediaCollectionList:
                  prevState.recentMediaCollectionList.map(rc => {
                    const p = patch.get(rc.mediaCollectionId)
                    return p ? { ...rc, ...p } : rc
                  }),
              }
            }

            case MediaLibraryActions.REMOVE_FILES: {
              const toRemove = new Set(action.payload)
              return {
                ...acc,
                fileList: prevState.fileList.filter(
                  f => !toRemove.has(f.fileId),
                ),
                recentFileFolderList: prevState.recentFileFolderList.filter(
                  item =>
                    'fileId' in item ? !toRemove.has(item.fileId) : true,
                ),
                selectedFiles: prevState.selectedFiles.filter(
                  f => !toRemove.has(f.fileId),
                ),
              }
            }

            case MediaLibraryActions.REMOVE_FILE_VERSIONS: {
              const toRemove = new Set(action.payload)
              return {
                ...acc,
                fileList: prevState.fileList.filter(
                  f => !toRemove.has(f.versionInfo.id),
                ),
                selectedFiles: prevState.selectedFiles.filter(
                  f => !toRemove.has(f.versionInfo.id),
                ),
              }
            }

            case MediaLibraryActions.REMOVE_FOLDERS: {
              const toRemove = new Set(action.payload)
              return {
                ...acc,
                folderList: prevState.folderList.filter(
                  d => !toRemove.has(d.folderId),
                ),
                recentFileFolderList: prevState.recentFileFolderList.filter(
                  item =>
                    'folderId' in item ? !toRemove.has(item.folderId) : true,
                ),
                selectedFolders: prevState.selectedFolders.filter(
                  d => !toRemove.has(d.folderId),
                ),
              }
            }

            case MediaLibraryActions.REMOVE_MEDIA_COLLECTIONS: {
              const toRemove = new Set(action.payload)
              return {
                ...acc,
                mediaCollectionList: prevState.mediaCollectionList.filter(
                  d => !toRemove.has(d.mediaCollectionId),
                ),
                recentMediaCollectionList:
                  prevState.recentMediaCollectionList.filter(item =>
                    'mediaCollectionId' in item
                      ? !toRemove.has(item.mediaCollectionId)
                      : true,
                  ),
                selectedMediaCollections:
                  prevState.selectedMediaCollections.filter(
                    d => !toRemove.has(d.mediaCollectionId),
                  ),
              }
            }

            case MediaLibraryActions.SET_FILE_ERROR:
              return { ...acc, fileListError: action.payload }

            case MediaLibraryActions.SET_FOLDER_LIST:
              return {
                ...acc,
                folderList: action.payload,
                selectedFolders: prevState.isAllSelected
                  ? action.payload
                  : prevState.selectedFolders,
              }

            case MediaLibraryActions.SET_FOLDER_ERROR:
              return { ...acc, folderListError: action.payload }

            case MediaLibraryActions.SET_DRAWER_FOLDER_LIST:
              return { ...acc, drawerFolderList: action.payload }

            case MediaLibraryActions.SET_DRAWER_FOLDER_ERROR:
              return { ...acc, drawerFolderListError: action.payload }

            case MediaLibraryActions.SET_MEDIA_COLLECTION_LIST:
              return {
                ...acc,
                mediaCollectionList: action.payload,
                selectedMediaCollections: prevState.isAllSelected
                  ? action.payload
                  : prevState.selectedMediaCollections,
              }

            case MediaLibraryActions.SET_MEDIA_COLLECTION_ERROR:
              return { ...acc, mediaCollectionListError: action.payload }

            case MediaLibraryActions.SET_MEDIA_COLLECTION_LOADING:
              return { ...acc, loadingMediaCollections: action.payload }

            case MediaLibraryActions.SET_FILE_LOADING:
              return { ...acc, loadingFiles: action.payload }

            case MediaLibraryActions.SET_FOLDER_LOADING:
              return { ...acc, loadingFolders: action.payload }

            case MediaLibraryActions.SET_DRAWER_FOLDER_LOADING:
              return { ...acc, loadingDrawerFolders: action.payload }

            case MediaLibraryActions.RESET_SEARCH:
              return {
                ...acc,
                fileList: [],
                recentFileFolderList: [],
                folderList: [],
                folderPage: 0,
                filePage: 0,
                loadingFiles: true,
                loadingFolders: true,
                isVersionIncludedInSearch: false,
                searchByTags: true,
                q: '',
                searchMode: 'keyword',
                searchInputMode: 'builder',
                fileType: '',
              }

            case MediaLibraryActions.RESET_MEDIA_COLLECTION_SEARCH:
              return {
                ...acc,
                recentMediaCollectionList: [],
                // mediaCollectionList: [],it has been done as mediaCollectionhook wont be initiated  on changing collection id to undefined.
                mediaCollectionPage: 0,
                loadingMediaCollections: true,
              }
            case MediaLibraryActions.RESET_DRAWER_SEARCH:
              return {
                ...acc,
                drawerSearchFilter: '',
                drawerSearchQuery: '',
              }
            case MediaLibraryActions.SET_Q:
              return {
                ...acc,
                q: action.payload,
              }
            case MediaLibraryActions.SET_SEARCH_MODE:
              return {
                ...acc,
                searchMode: action.payload,
              }
            case MediaLibraryActions.SET_SEARCH_INPUT_MODE:
              return {
                ...acc,
                searchInputMode: action.payload,
              }
            case MediaLibraryActions.SET_SEARCH_BY_TAGS:
              return {
                ...acc,
                searchByTags: action.payload,
              }
            case MediaLibraryActions.SET_IS_VERSION_INCLUDED_IN_SEARCH:
              return {
                ...acc,
                isVersionIncludedInSearch: action.payload,
              }
            case MediaLibraryActions.SET_SEARCH_FILE_TYPE:
              return {
                ...acc,
                fileType: action.payload,
              }
            case MediaLibraryActions.SET_SEARCH_QUERY:
              return {
                ...acc,
                fileList: [],
                recentFileFolderList: [],
                folderList: [],
                folderPage: 0,
                filePage: 0,
                loadingFiles: true,
                loadingFolders: true,
              }
            case MediaLibraryActions.SET_SEARCH_QUERY_JSON:
              return {
                ...acc,
                searchQueryJson: action.payload,
                fileList: [],
                recentFileFolderList: [],
                folderList: [],
                folderPage: 0,
                filePage: 0,
                loadingFiles: true,
                loadingFolders: true,
              }
            case MediaLibraryActions.SET_MEDIA_COLLECTION_SEARCH_QUERY_JSON:
              return {
                ...acc,
                searchQueryJson: action.payload,
              }
            case MediaLibraryActions.SET_DRAWER_SEARCH_QUERY:
              return {
                ...acc,
                drawerSearchQuery: action.payload,
              }
            case MediaLibraryActions.SET_MEDIA_COLLECTION_SEARCH_QUERY:
              return {
                ...acc,
                mediaCollectionPage: 0,
                loadingMediaCollections: true,
                mediaCollectionSearchQuery: action.payload,
              }
            case MediaLibraryActions.SET_DRAWER_SEARCH_FILTER:
              return {
                ...acc,
                drawerSearchFilter: action.payload,
              }
            case MediaLibraryActions.SET_VIEW:
              return {
                ...acc,
                selectedFileOrFolderIds: [],
              }
            case MediaLibraryActions.SET_SORT_DIRECTION:
              return {
                ...acc,
                folderList: [],
                fileList: [],
                recentFileFolderList: [],
                recentMediaCollectionList: [],
                // mediaCollectionList: [],it has been done as medialibraryhook wont be initiated again as just two sorting parametes are given.
                folderPage: 0,
                filePage: 0,
                mediaCollectionPage: 0,
                selectedFileOrFolderIds: [],
                loadingFiles: true,
                loadingFolders: true,
                loadingMediaCollections: true,
              }
            case MediaLibraryActions.SET_SECONDARY_SORT_DIRECTION:
              return {
                ...acc,
                secondarySortDescriptors: {
                  ...prevState.secondarySortDescriptors,
                  sortDirection: action.payload,
                },
                secondaryFolderPage: 0,
                secondarySort: `${action.payload}_${prevState.secondarySortDescriptors.sortParam}`,
                loadingDrawerFolders: true,
              }
            case MediaLibraryActions.SET_SORT_PARAM:
              return {
                ...acc,
                folderList: [],
                fileList: [],
                recentFileFolderList: [],
                recentMediaCollectionList: [],
                // mediaCollectionList: [], it has been done as medialibraryhook wont be initiated again as just two sorting parametes are given.
                folderPage: 0,
                filePage: 0,
                mediaCollectionPage: 0,
                selectedFileOrFolderIds: [],
                loadingFiles: true,
                loadingFolders: true,
                loadingMediaCollections: true,
              }
            case MediaLibraryActions.SET_SORT: {
              const [sortDirection, sortParam] = action.payload.split('_')
              return {
                ...acc,
                sortDescriptors: {
                  sortDirection: sortDirection as MLSortDirection,
                  sortParam: sortParam as MLSortParams,
                },
                folderList: [],
                fileList: [],
                mediaCollectionList: [],
                recentMediaCollectionList: [],
                recentFileFolderList: [],
                folderPage: 0,
                filePage: 0,
                mediaCollectionPage: 0,
                selectedFileOrFolderIds: [],
                loadingFiles: true,
                loadingFolders: true,
                loadingMediaCollections: true,
              }
            }
            case MediaLibraryActions.SET_SECONDARY_SORT_PARAM:
              return {
                ...acc,
                secondarySortDescriptors: {
                  ...prevState.secondarySortDescriptors,
                  sortParam: action.payload,
                },
                secondaryFolderPage: 0,
                secondarySort: `${prevState.secondarySortDescriptors.sortDirection}_${action.payload}`,
                loadingDrawerFolders: true,
              }
            case MediaLibraryActions.LOAD_MORE_FOLDERS:
              return {
                ...acc,
                folderPage: prevState.folderPage + 1,
              }

            case MediaLibraryActions.LOAD_MORE_SECONDARY_FOLDERS:
              return {
                ...acc,
                secondaryFolderPage: prevState.secondaryFolderPage + 1,
              }

            case MediaLibraryActions.RESET_FOLDER_PAGE:
              return {
                ...acc,
                folderPage: 0,
                folderList: [],
                recentFileFolderList: [],
                loadingFolders: true,
              }

            case MediaLibraryActions.RESET_MEDIA_COLLECTIONS_PAGE:
              return {
                ...acc,
                mediaCollectionPage: 0,
                mediaCollectionList: [],
                recentMediaCollectionList: [],
                loadingMediaCollections: true,
              }

            case MediaLibraryActions.RESET_SECONDARY_FOLDER_PAGE:
              return {
                ...acc,
                secondaryFolderPage: 0,
              }

            case MediaLibraryActions.LOAD_MORE_FILES:
              return {
                ...acc,
                filePage: prevState.filePage + 1,
              }

            case MediaLibraryActions.LOAD_MORE_MEDIA_COLLECTIONS:
              return {
                ...acc,
                mediaCollectionPage: prevState.mediaCollectionPage + 1,
              }

            case MediaLibraryActions.RESET_FILE_PAGE:
              return {
                ...acc,
                filePage: 0,
                fileList: [],
                recentFileFolderList: [],
                loadingFiles: true,
              }

            case MediaLibraryActions.SELECT_FILE:
              return {
                ...acc,
                selectedFolders: [],
                selectedFiles: [action.payload.file],
              }
            case MediaLibraryActions.SELECT_FOLDER:
              return {
                ...acc,
                selectedFiles: [],
                selectedFolders: [action.payload.folder],
              }

            case MediaLibraryActions.SET_SELECTED_BREADCRUMB_FOLDER:
              return {
                ...acc,
                selectedBreadcrumbFolder: action.payload.folder,
              }

            case MediaLibraryActions.SET_SELECTED_BREADCRUMB_MEDIA_COLLECTION:
              return {
                ...acc,
                selectedBreadcrumbMediaCollection:
                  action.payload.mediaCollection,
              }

            case MediaLibraryActions.SELECT_MEDIA_COLLECTION:
              if (prevState.selectedBreadcrumbMediaCollection) {
                return {
                  ...acc,
                  selectedMediaCollections: [action.payload.mediaCollection],
                }
              }
              if (
                action.payload.event.ctrlKey ||
                action.payload.event.metaKey
              ) {
                if (
                  prevState.selectedMediaCollections
                    .map(x => x.mediaCollectionId)
                    .includes(action.payload.mediaCollection.mediaCollectionId)
                ) {
                  return {
                    ...acc,
                    selectedMediaCollections:
                      prevState.selectedMediaCollections.filter(
                        x =>
                          x.mediaCollectionId !==
                          action.payload.mediaCollection.mediaCollectionId,
                      ),
                  }
                }
                return {
                  ...acc,
                  selectedMediaCollections: [
                    ...prevState.selectedMediaCollections,
                    action.payload.mediaCollection,
                  ],
                }
              } else if (action.payload.event.shiftKey) {
                // Handle Shift Key here
              }

              return {
                ...acc,
                selectedMediaCollections: [action.payload.mediaCollection],
              }

            case MediaLibraryActions.SET_SELECTED_FILES:
              if (prevState.selectedBreadcrumbFolder) {
                return {
                  ...acc,
                  selectedFiles: action.payload,
                  selectedFolders: [],
                }
              }
              return {
                ...acc,
                selectedFiles: action.payload,
              }

            case MediaLibraryActions.SET_DETAIL_PAGE_VERSIONS:
              return {
                ...acc,
                detailPageVersions: action.payload,
              }

            case MediaLibraryActions.SET_SELECTED_FOLDERS:
              if (prevState.selectedBreadcrumbFolder) {
                action.payload = action.payload.filter(
                  folder =>
                    folder.folderId !==
                    prevState.selectedBreadcrumbFolder?.folderId,
                )
                return {
                  ...acc,
                  selectedFolders: action.payload,
                }
              }
              return {
                ...acc,
                selectedFolders: action.payload,
              }
            case MediaLibraryActions.SET_SELECTED_MEDIA_COLLECTIONS:
              return {
                ...acc,
                selectedMediaCollections: action.payload,
              }

            case MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS:
              return {
                ...acc,
                selectedFiles: [],
                selectedFolders: [],
              }

            case MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS:
              return {
                ...acc,
                selectedMediaCollections: [],
              }

            case MediaLibraryActions.OPEN_CONTEXT_MENU:
              return {
                ...acc,
                contextMenu: {
                  visible: true,
                  position: {
                    x: action.payload.x,
                    y: action.payload.y,
                  },
                },
              }

            case MediaLibraryActions.CLOSE_CONTEXT_MENU:
              return {
                ...acc,
                contextMenu: {
                  visible: false,
                },
              }

            case MediaLibraryActions.SET_DRAWER_FOLDER:
              return {
                ...acc,
                drawerFolderPath: action.payload,
              }

            case MediaLibraryActions.SET_DRAWER_FOLDER_PERMISSION:
              return {
                ...acc,
                drawerFolderPermission: action.payload,
              }

            case MediaLibraryActions.SET_DRAWER_VIEWABLE_PARENT:
              return {
                ...acc,
                drawerViewableParent: action.payload,
              }

            case MediaLibraryActions.SET_COPY_ITEMS:
              return {
                ...acc,
                copyItems: action.payload,
              }

            case MediaLibraryActions.SET_MOVE_ITEMS:
              return {
                ...acc,
                moveItems: action.payload,
              }

            case MediaLibraryActions.SET_RENAME_ITEM:
              return {
                ...acc,
                renameFolderItem: action.payload,
              }

            case MediaLibraryActions.ADD_OPERATION:
              return {
                ...acc,
                operations: [...prevState.operations, action.payload],
              }

            case MediaLibraryActions.UPDATE_OPERATION:
              return {
                ...acc,
                operations: action.payload,
              }

            case MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH:
              return {
                ...acc,
                openAdvancedSearch: action.payload,
              }

            case MediaLibraryActions.THREAD_LIST_UI_LOADED:
              return {
                ...acc,
                threadListUiLoaded: true,
              }

            case MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH:
              return {
                ...acc,
                openSemanticImageSearch: action.payload,
              }

            case MediaLibraryActions.SET_UNSEEN_NOTIFICATIONS_COUNT:
              return {
                ...acc,
                unseenNotificationsCount: action.payload,
              }

            case MediaLibraryActions.SET_GENERATED_IMAGES_DIMENSION:
              return {
                ...acc,
                generateImageDimension: action.payload,
              }

            case MediaLibraryActions.SET_GENERATED_IMAGES_NUMBER:
              return {
                ...acc,
                generateImageNumber: action.payload,
              }

            case MediaLibraryActions.SELECT_ALL:
              if (action.payload === '/search') {
                return {
                  ...acc,
                  selectedFiles: Array.isArray(prevState.fileList)
                    ? [
                        ...prevState.fileList,
                        ...prevState.recentFileFolderList.filter(
                          item => item.type === 'file',
                        ),
                      ]
                    : [],
                  selectedFolders: Array.isArray(prevState.folderList)
                    ? [
                        ...prevState.folderList,
                        ...prevState.recentFileFolderList.filter(
                          item => item.type === 'folder',
                        ),
                      ]
                    : [],
                  selectedMediaCollections: Array.isArray(
                    prevState.mediaCollectionList,
                  )
                    ? [
                        ...prevState.mediaCollectionList,
                        ...prevState.recentMediaCollectionList,
                      ]
                    : [],
                  isAllSelected: true,
                }
              }
              if (
                !mediaCollectionId &&
                mediaCollectionRoute &&
                folderPath === '/'
              ) {
                return {
                  ...acc,
                  selectedMediaCollections: Array.isArray(
                    prevState.mediaCollectionList,
                  )
                    ? [
                        ...prevState.mediaCollectionList,
                        ...prevState.recentMediaCollectionList,
                      ]
                    : [],
                  isAllSelected: true,
                }
              }
              return {
                ...acc,
                selectedFiles: Array.isArray(prevState.fileList)
                  ? [
                    ...prevState.fileList,
                    ...prevState.recentFileFolderList.filter(
                      item => item.type === 'file',
                    ),
                  ]
                  : [],
                selectedFolders: Array.isArray(prevState.folderList)
                  ? [
                    ...prevState.folderList,
                    ...prevState.recentFileFolderList.filter(
                      item => item.type === 'folder',
                    ),
                  ]
                  : [],
                isAllSelected: true,
              }
              break;
            case MediaLibraryActions.SET_IS_ALL_SELECTED_FALSE:
              return {
                ...acc,
                isAllSelected: false,
              }
            case MediaLibraryActions.SET_DISABLE_DETAIL_NAVIGATION:
              return {
                ...acc,
                disableDetailNavigation: action.payload,
              }
            default:
              return {
                ...acc,
              }
          }
        },
        {},
      )

      return {
        ...prevState,
        ...stateUpdate,
      }
    },
    initialState,
    arg => arg,
  )

  useEffect(() => {
    if (state.isAllSelected) {
      const totalAssetsCount =
        state.fileList.length +
        state.folderList.length +
        state.mediaCollectionList.length +
        state.recentFileFolderList.length +
        state.recentMediaCollectionList.length
      const totalSelectedCount =
        state.selectedFiles.length +
        state.selectedFolders.length +
        state.selectedMediaCollections.length
      if (totalAssetsCount !== totalSelectedCount) {
        stateDispatch({ type: MediaLibraryActions.SET_IS_ALL_SELECTED_FALSE })
      }
    }
  }, [
    state.fileList,
    state.folderList,
    state.mediaCollectionList,
    state.selectedFiles,
    state.selectedFolders,
    state.selectedMediaCollections,
    state.recentFileFolderList,
    state.recentMediaCollectionList,
    state.isAllSelected,
  ])

  useEffect(() => {
    stateDispatch({ type: MediaLibraryActions.SET_IS_ALL_SELECTED_FALSE })
  },[route, folderPath, mediaCollectionRoute, mediaCollectionId])


  // added queryParams?.isVersionIncludedInSearch since calls to folders and media collections are suppressed, we need to reset the page state if the version is included in search
  useEffect(() => {
    // in public link UI, when at the "All Collections" page, don't do this, because the loading states never return to being false
    if (
      isPublicLink &&
      mediaCollectionRoute &&
      typeof mediaCollectionId === 'undefined' &&
      folderPath === '/'
    ) {
      return
    }
    stateDispatch([
      {
        type: MediaLibraryActions.RESET_FOLDER_PAGE,
      },
      {
        type: MediaLibraryActions.RESET_FILE_PAGE,
      },
      {
        type: MediaLibraryActions.RESET_MEDIA_COLLECTIONS_PAGE,
      },
    ])
  }, [folderPath, mediaCollectionRoute, route, mediaCollectionId, isPublicLink, queryParams?.isVersionIncludedInSearch])

  const folderPermissionResponse = useSetFolderPermission({
    folderPath: urlState.folderPath,
    onData: data => {
      stateDispatch({
        type: MediaLibraryActions.SET_FOLDER_PERMISSION,
        payload: data,
      })
    },
    onLoading: loading => {
      stateDispatch({
        type: MediaLibraryActions.SET_FOLDER_PERMISSION_LOADING,
        payload: loading,
      })
    },
  })

  const viewableFolderResponse = useSetViewableParent({
    folderPath: urlState.folderPath,
    mediaCollectionId: urlState.currentMediaCollection,
    onData: data => {
      stateDispatch({
        type: MediaLibraryActions.SET_VIEWABLE_PARENT,
        payload: data,
      })
    },
    onLoading: loading => {
      stateDispatch({
        type: MediaLibraryActions.SET_VIEWABLE_PARENT_LOADING,
        payload: loading,
      })
    },
  })

  const filesResponse = useMediaLibrarySearch<MediaLibrarySearchFileResponse>({
    path:
      urlState.searchQuery === '' &&
       urlState.q === "" &&
      !urlState.isVersionIncludedInSearch &&
      !urlState.fileType
        ? folderPath
        : null,
    skip: state.filePage * filesPerPage,
    collectionId:
      urlState.searchQuery === ''
        ? urlState.currentMediaCollection
          ? urlState.currentMediaCollection
          : undefined
        : undefined,
    limit: filesPerPage,
    sort: urlState.sort,
    minScore: urlState.minScore,
    semanticSupported: true,
    q: urlState.q,
    searchMode: urlState.searchMode,
    searchInputMode: urlState.searchInputMode,
    searchByTags: urlState.searchByTags,
    fileType: urlState.fileType,
    suppressRequest: suppressSearchRequests || directDetailRoute || (route === 'media-collections' && !urlState.currentMediaCollection),
    assetType: 'file',
    isVersionIncludedInSearch: urlState.isVersionIncludedInSearch,
    searchQuery: urlState.searchQuery,
    onData: data => {
      if (!data.success) {
        stateDispatch([
          {
            type: MediaLibraryActions.SET_FILE_ERROR,
            payload:
              (data.error as { message?: string })?.message ??
              'Something went wrong',
          },
          { type: MediaLibraryActions.SET_FILE_LIST, payload: [] },
        ])
      } else {
        stateDispatch([
          {
            type: MediaLibraryActions.SET_FILE_ERROR,
            payload: '',
          },
          {
            type: MediaLibraryActions.SET_FILE_LIST,
            payload: data.items,
          },
        ])
      }
    },
    onLoading: loading => {
      stateDispatch({
        type: MediaLibraryActions.SET_FILE_LOADING,
        payload: loading,
      })
    },
  })

  const foldersResponse =
    useMediaLibrarySearch<MediaLibrarySearchFolderResponse>({
      path: queryParams?.searchQuery === '' && queryParams?.q === "" ? folderPath : null,
      skip: state.folderPage * foldersPerPage,
      collectionId:
        queryParams?.searchQuery === ''
          ? urlState.currentMediaCollection
            ? urlState.currentMediaCollection
            : undefined
          : undefined,
      limit: foldersPerPage,
      sort: urlState.sort,
      q: urlState.q,
      searchMode: urlState.searchMode,
      searchInputMode: urlState.searchInputMode,
      searchByTags: urlState.searchByTags,
      assetType: 'folder',
      isVersionIncludedInSearch: false,
      searchQuery: queryParams?.searchQuery ?? '',
      fileType: urlState.fileType,
      // suppress the request if only version is included in search
      // (applicable for whole media library search or folder restricted search)
      suppressRequest:
        suppressSearchRequests ||
        directDetailRoute ||
        (route === 'media-collections' && !urlState.currentMediaCollection) ||
        urlState.isVersionIncludedInSearch,
      onData: data => {
        if (!data.success) {
          stateDispatch([
            {
              type: MediaLibraryActions.SET_FOLDER_ERROR,
              payload:
                (data.error as { message?: string })?.message ??
                'Something went wrong',
            },
            {
              type: MediaLibraryActions.SET_FOLDER_LIST,
              payload: [],
            },
          ])
        } else {
          stateDispatch([
            {
              type: MediaLibraryActions.SET_FOLDER_ERROR,
              payload: '',
            },
            {
              type: MediaLibraryActions.SET_FOLDER_LIST,
              payload: data.items,
            },
          ])
        }
      },
      onLoading: loading => {
        stateDispatch({
          type: MediaLibraryActions.SET_FOLDER_LOADING,
          payload: loading,
        })
      },
    })

  const mediaCollectionsResponse =
    useMediaLibrarySearch<MediaLibrarySearchCollectionResponse>({
      path: '',
      skip: state.mediaCollectionPage * mediaCollectionsPerPage,
      collectionId: true,
      limit: mediaCollectionsPerPage,
      sort: [
        'DESC_CREATED',
        'ASC_CREATED',
        'DESC_NAME',
        'ASC_NAME',
        'DESC_RELEVANCE',
        'ASC_RELEVANCE',
      ].includes(urlState.sort)
        ? urlState.sort
        : 'DESC_CREATED',
      q: urlState.q,
      searchMode: urlState.searchMode,
      searchInputMode: urlState.searchInputMode,
      searchByTags: urlState.searchByTags,
      assetType: 'media-collection',
      isVersionIncludedInSearch: false,
      searchQuery: queryParams?.searchQuery ?? '',
      suppressRequest:
        suppressSearchRequests ||
        directDetailRoute ||
        route === 'media-library' ||
        urlState.isVersionIncludedInSearch ||
        Boolean(
          route === 'media-collections' && urlState.currentMediaCollection,
        ),
        // Boolean(urlState.searchMode && urlState.searchMode !== 'keyword'),
      fileType: urlState.fileType,
      onData: data => {
        // // Ignore updates when route is 'media-library' (collections should be empty)
        if (route === 'media-library') {
          stateDispatch({
            type: MediaLibraryActions.SET_MEDIA_COLLECTION_LIST,
            payload: [],
          })
          return
        }
        if (!data.success) {
          stateDispatch([
            {
              type: MediaLibraryActions.SET_MEDIA_COLLECTION_ERROR,
              payload:
                (data.error as { message?: string })?.message ??
                'Something went wrong',
            },
            {
              type: MediaLibraryActions.SET_MEDIA_COLLECTION_LIST,
              payload: [],
            },
          ])
        } else {
          stateDispatch([
            {
              type: MediaLibraryActions.SET_MEDIA_COLLECTION_ERROR,
              payload: ''
            },
            {
              type: MediaLibraryActions.SET_MEDIA_COLLECTION_LIST,
              payload: data.items,
            },
          ])
        }
      },
      onLoading: loading => {
        stateDispatch({
          type: MediaLibraryActions.SET_MEDIA_COLLECTION_LOADING,
          payload: loading,
        })
      },
    })

  const drawerFolderResponse =
    useMediaLibrarySearch<MediaLibrarySearchFolderResponse>({
      path: state.drawerSearchQuery === '' ? state.drawerFolderPath : null,
      skip: state.secondaryFolderPage * secondaryFoldersPerPage,
      collectionId: undefined,
      isDrawerSearch: true,
      limit: secondaryFoldersPerPage,
      sort: state.secondarySort,
      q: '',
      searchMode: '',
      searchInputMode: '',
      assetType: 'folder',
      isVersionIncludedInSearch: false,
      searchQuery: state.drawerSearchQuery ?? '',
      suppressRequest: directDetailRoute,
      onData: data => {
        if (!data.success) {
          stateDispatch([
            {
              type: MediaLibraryActions.SET_DRAWER_FOLDER_ERROR,
              payload:
                (data.error as { message?: string })?.message ??
                'Something went wrong',
            },
            {
              type: MediaLibraryActions.SET_DRAWER_FOLDER_LIST,
              payload: [],
            },
          ])
        } else {
          stateDispatch([
            {
              type: MediaLibraryActions.SET_DRAWER_FOLDER_ERROR,
              payload: '',
            },
            {
              type: MediaLibraryActions.SET_DRAWER_FOLDER_LIST,
              payload: data.items,
            },
          ])
        }
      },
      onLoading: loading => {
        stateDispatch({
          type: MediaLibraryActions.SET_DRAWER_FOLDER_LOADING,
          payload: loading,
        })
      },
    })

  const drawerFolderPermissionResponse = useSetFolderPermission({
    folderPath: state.drawerFolderPath,
    onData: data => {
      stateDispatch({
        type: MediaLibraryActions.SET_DRAWER_FOLDER_PERMISSION,
        payload: data,
      })
    },
    onLoading: loading => {
      stateDispatch({
        type: MediaLibraryActions.SET_DRAWER_FOLDER_PERMISSION_LOADING,
        payload: loading,
      })
    },
  })

  const drawerViewableFolderResponse = useSetViewableParent({
    folderPath: state.drawerFolderPath,
    mediaCollectionId: undefined,
    onData: data => {
      stateDispatch({
        type: MediaLibraryActions.SET_DRAWER_VIEWABLE_PARENT,
        payload: data,
      })
    },
    onLoading: loading => {
      stateDispatch({
        type: MediaLibraryActions.SET_DRAWER_VIEWABLE_PARENT_LOADING,
        payload: loading,
      })
    },
  })

  useGetBulkJobStatus({
    bulkJobIds: state.copyItems.map(({ jobId }) => jobId).filter(Boolean) as string[],
    onData: (data: StatusResponse[]) => {
      state.copyItems.forEach((item, index) => {
        const job = data.find(_job => _job._id === item.jobId)
        if (job) {
          const status: OperationStatus = ["INITIATED", "PROCESSING"].includes(job.status) ? OperationStatus.PENDING : job.status.toLowerCase() as OperationStatus
          state.copyItems[index] = {
            ...item,
            status: status,
          }
        }
      })

      stateDispatch({
        type: MediaLibraryActions.SET_COPY_ITEMS,
        payload: state.copyItems,
      })
    }
  })

  useGetBulkJobStatus({
    bulkJobIds: state.moveItems.map(({ jobId }) => jobId).filter(Boolean) as string[],
    onData: (data: StatusResponse[]) => {
      state.moveItems.forEach((item, index) => {
        const job = data.find(_job => _job._id === item.jobId)
        if (job) {
          const status: OperationStatus = ["INITIATED", "PROCESSING"].includes(job.status) ? OperationStatus.PENDING : job.status.toLowerCase() as OperationStatus
          state.moveItems[index] = {
            ...item,
            status: status,
          }
        }
      })

      stateDispatch({
        type: MediaLibraryActions.SET_MOVE_ITEMS,
        payload: state.moveItems,
      })
    }
  })

  useGetBulkJobStatus({
    bulkJobIds: state.renameFolderItem.map(({ jobId }) => jobId).filter(Boolean) as string[],
    onData: (data: StatusResponse[]) => {
      state.renameFolderItem.forEach((item, index) => {
        const job = data.find(_job => _job._id === item.jobId)
        if (job) {
          const status: OperationStatus = ["INITIATED", "PROCESSING"].includes(job.status) ? OperationStatus.PENDING : job.status.toLowerCase() as OperationStatus
          state.renameFolderItem[index] = {
            ...item,
            status: status,
          }
        }
      })

      stateDispatch({
        type: MediaLibraryActions.SET_RENAME_ITEM,
        payload: state.renameFolderItem,
      })
    }
  })

  const loading = useMemo(
    () => state.loadingFiles && state.loadingFolders,
    [state.loadingFiles, state.loadingFolders],
  )

  const refetchFiles = useCallback(() => {
    stateDispatch({ type: MediaLibraryActions.RESET_FILE_PAGE })

    // use the new page number directly
    const newFilePage = 0

    return filesResponse.refetch({ skip: newFilePage * filesPerPage })
  }, [state])

  const refetchFolders = useCallback(() => {
    stateDispatch({ type: MediaLibraryActions.RESET_FOLDER_PAGE })

    // use the new page number directly
    const newFolderPage = 0

    return foldersResponse.refetch({ skip: newFolderPage * foldersPerPage })
  }, [state])

  const refetchMediaCollections = useCallback(() => {
    stateDispatch({ type: MediaLibraryActions.RESET_MEDIA_COLLECTIONS_PAGE })

    // use the new page number directly
    const newMediaCollectionPage = 0

    return mediaCollectionsResponse.refetch({
      skip: newMediaCollectionPage * mediaCollectionsPerPage,
    })
  }, [state])

  const refetchViewableParent = useCallback(
    () => viewableFolderResponse.refetch(),
    [state, viewableFolderResponse.refetch],
  )

  const refetchFolderPermission = useCallback(
    () => folderPermissionResponse.refetch(),
    [state, folderPermissionResponse.refetch],
  )
  // why this change is done?
  // The crux of the problem is that after resetting the page numbers
  // (e.g., filePage, folderPage, mediaCollectionPage) to zero using stateDispatch,
  // you immediately call the refetch functions, which rely on these state values.
  // Because the state updates are asynchronous,
  // the refetch functions may still use the old state values, leading to inconsistent behavior.
  const refetch = useCallback(() => {
    // Dispatch actions to reset pages
    stateDispatch([
      { type: MediaLibraryActions.RESET_FILE_PAGE },
      { type: MediaLibraryActions.RESET_FOLDER_PAGE },
      { type: MediaLibraryActions.RESET_MEDIA_COLLECTIONS_PAGE },
    ])

    // Use the new page numbers directly
    const newFilePage = 0
    const newFolderPage = 0
    const newMediaCollectionPage = 0

    // Call refetch functions with overridden skip values
    void foldersResponse.refetch({ skip: newFolderPage * foldersPerPage })
    void filesResponse.refetch({ skip: newFilePage * filesPerPage })
    return mediaCollectionsResponse.refetch({
      skip: newMediaCollectionPage * mediaCollectionsPerPage,
    })
  }, [
    filesResponse.refetch,
    foldersResponse.refetch,
    mediaCollectionsResponse.refetch,
    state,
  ])

  const abortFilesRequest = useCallback(
    () => void filesResponse.abort(),
    [state],
  )

  const abortFoldersRequest = useCallback(
    () => void foldersResponse.abort(),
    [state],
  )

  const abortMediaCollectionsRequest = useCallback(
    () => void mediaCollectionsResponse.abort(),
    [state],
  )

  const dispatch: React.Dispatch<
    MediaLibraryActionType | MediaLibraryActionType[]
  > = useMemo(
    () => (inputActions: MediaLibraryActionType | MediaLibraryActionType[]) => {
      const actions: MediaLibraryActionType[] = Array.isArray(inputActions)
        ? inputActions
        : [inputActions]

      const queryParamsUpdate = actions.reduce((acc, action) => {
        switch (action.type) {
          case MediaLibraryActions.RESET_SEARCH:
            return {
              ...acc,
              q: '',
              searchQuery: '',
              fileType: "",
              searchByTags: 'true',
              isVersionIncludedInSearch: 'false',
              minScore: undefined,
              searchInputMode: 'builder',
              searchMode: 'keyword',
              sort: `${MLSortDirection.DESC}_${MLSortParams.CREATED}`,
            }
          case MediaLibraryActions.RESET_MEDIA_COLLECTION_SEARCH:
            return {
              ...acc,
              q: '',
              searchQuery: '',
            }
          case MediaLibraryActions.RESET_MIN_SCORE:
            return {
              ...acc,
              minScore: undefined,
            }
          case MediaLibraryActions.SET_MIN_SCORE:
            return {
              ...acc,
              minScore: action.payload.toString(),
            }
          case MediaLibraryActions.SET_Q:
            return {
              ...acc,
              q: action.payload,
            }
          case MediaLibraryActions.SET_SEARCH_MODE:
            return {
              ...acc,
              searchMode: action.payload,
            }
          case MediaLibraryActions.SET_SEARCH_INPUT_MODE:
            return {
              ...acc,
              searchInputMode: action.payload,
            }
          case MediaLibraryActions.SET_SEARCH_FILE_TYPE:
            return {
              ...acc,
              fileType: action.payload,
            }
          case MediaLibraryActions.SET_SEARCH_BY_TAGS:
            return {
              ...acc,
              searchByTags: String(action.payload),
            }
          case MediaLibraryActions.SET_IS_VERSION_INCLUDED_IN_SEARCH:
            return {
              ...acc,
              isVersionIncludedInSearch: String(action.payload),
            }
          case MediaLibraryActions.SET_SEARCH_QUERY:
            return {
              ...acc,
              searchQuery: action.payload,
            }
          case MediaLibraryActions.SET_MEDIA_COLLECTION_SEARCH_QUERY:
            return {
              ...acc,
              searchQuery: action.payload,
            }
          case MediaLibraryActions.SET_MEDIA_COLLECTION_SEARCH_QUERY_JSON:
            return {
              ...acc,
              searchQueryJson: action.payload,
            }       
          case MediaLibraryActions.SET_SORT_DIRECTION:
            return {
              ...acc,
              sort: `${action.payload}_${urlState.sortDescriptors.sortParam}`,
            }
          case MediaLibraryActions.SET_SORT_PARAM:
            return {
              ...acc,
              sort: `${urlState.sortDescriptors.sortDirection}_${action.payload}`,
            }
          case MediaLibraryActions.SET_SORT:
            return {
              ...acc,
              sort: action.payload,
            }
          case MediaLibraryActions.SET_VIEW:
            return {
              ...acc,
              view: action.payload,
            }
          default:
            return acc
        }
      }, {})

      if (!isEmpty(queryParamsUpdate)) {
        setQueryParams?.(queryParamsUpdate)
      }

      stateDispatch(inputActions)
    },
    [setQueryParams, stateDispatch, urlState],
  )

  return (
    <MLContext.Provider
      value={[
        {
          ...state,
          ...urlState,
          loading,
          loadingFiles: state.loadingFiles && filesResponse.loading,
          loadingFolders: state.loadingFolders && foldersResponse.loading,
          loadingDrawerFolders: state.loadingDrawerFolders,
          loadingMediaCollections:
            state.loadingMediaCollections && mediaCollectionsResponse.loading,
          loadingFolderPermission:
            state.loadingFolderPermission && folderPermissionResponse.loading,
          loadingDrawerFolderPermission:
            state.loadingDrawerFolderPermission &&
            drawerFolderPermissionResponse.loading,
          mediaCollectionRoute,
          route,
          loadingViewableParent: state.loadingViewableParent && viewableFolderResponse.loading,
          refetchFiles,
          refetchFolders,
          refetchMediaCollections,
          refetchViewableParent,
          refetchFolderPermission,
          refetch,
          refetchDrawerFolders: () => {
            stateDispatch({
              type: MediaLibraryActions.RESET_SECONDARY_FOLDER_PAGE,
            })
            return drawerFolderResponse.refetch()
          },
          abortFilesRequest,
          abortFoldersRequest,
          abortMediaCollectionsRequest,
          urlState,
          queryParams,
        },
        dispatch,
      ]}
    >
      {children}
    </MLContext.Provider>
  )
}
